import React from 'react';
import { GridCellProps } from '@progress/kendo-react-grid';
import { CustomFieldCell } from '../CustomFieldCell';
import { ContentTypeFieldResponse } from '@cs-cms/types';
import { RemoveCell } from '../../../grid/customCells/RemoveCell';
import type { FileWithPerFileMetadata } from '../../fieldInteractions/fieldInteractionsTypes';
import { getFileItemKey } from '../../fieldInteractions/FieldInteractionsHelper';

enum GridFileFields {
  METADATA_PREFIX = 'perFileMetadata.'
}

export const NameCell = (props: GridCellProps) => {
  const dataItem = props.dataItem;
  const file = dataItem?.file || dataItem;
  return <td title={file?.name}>{file?.name}</td>;
};

interface CellHandlers {
  perFileFields: ContentTypeFieldResponse[];
  getPerFileFieldChoices: (fileKey: string, field: ContentTypeFieldResponse) => string[];
  isPerFileFieldEnabled: (fileKey: string, field: ContentTypeFieldResponse) => boolean;
  isPerFileFieldLoading: (fileKey: string, field: ContentTypeFieldResponse) => boolean;
  handleRemove: (fileIndex: number, fileKey: string) => void;
}

export const createRemoveCellWrapper = (handlersRef: React.RefObject<CellHandlers>) => {
  return (props: GridCellProps) => {
    const fileKey = getFileItemKey(props.dataItem as FileWithPerFileMetadata);

    return (
      <RemoveCell
        dataItem={props.dataItem}
        field={props.field}
        dataIndex={props.dataIndex}
        id={props.id}
        columnPosition={props.columnPosition}
        onChange={props.onChange}
        className={props.className}
        colSpan={props.colSpan}
        ariaColumnIndex={props.ariaColumnIndex}
        isSelected={props.isSelected}
        style={props.style}
        rowType={props.rowType}
        level={props.level}
        expanded={props.expanded}
        onClick={() => handlersRef.current.handleRemove(props.dataIndex, fileKey)}
      />
    );
  };
};

export const createCustomFieldCellWrapper = (handlersRef: React.RefObject<CellHandlers>) => {
  return (props: GridCellProps) => {
    const { field } = props;

    if (!field || !field.startsWith(GridFileFields.METADATA_PREFIX)) {
      return <td className={props.className} />;
    }

    const fieldName = field.replace(GridFileFields.METADATA_PREFIX, '');
    const ctField = handlersRef.current.perFileFields.find(f => f.name === fieldName);

    if (!ctField) {
      return <td className={props.className}>Field not found</td>;
    }

    return (
      <td className={props.className}>
        <CustomFieldCell
          dataItem={props.dataItem}
          field={props.field}
          dataIndex={props.dataIndex}
          id={props.id}
          columnPosition={props.columnPosition}
          onChange={props.onChange}
          className={props.className}
          colSpan={props.colSpan}
          ariaColumnIndex={props.ariaColumnIndex}
          isSelected={props.isSelected}
          style={props.style}
          rowType={props.rowType}
          level={props.level}
          expanded={props.expanded}
          ctField={ctField}
          getPerFileFieldChoices={handlersRef.current.getPerFileFieldChoices}
          isPerFileFieldEnabled={handlersRef.current.isPerFileFieldEnabled}
          isPerFileFieldLoading={handlersRef.current.isPerFileFieldLoading}
        />
      </td>
    );
  };
};
